import type { ReactNode } from 'react';
import { t, tn } from '@app/i18n';
import { Icon, type IconName } from '@zarya/theme';
import { InfoTip } from '@zarya/ui';
import { ReadingNote } from '@app/reading';
import type { Overview } from './Overview.api';
import { cardFailure, failureText } from './degradation';
import s from './Overview.module.css';

/**
 * Os quatro indicators de T3 (FR-094), na ordem do protótipo (`rOverview`):
 * Identidades · SecurityReady · Aprovações pendentes · Decisões DENY (24 h).
 *
 * SecurityReady e DENY dependem do middleware e degradam sozinhos: o value vira
 * "—" e o detail diz qual fonte não pôde ser lida (FR-095).
 */

type Tom = 'pri' | 'ok' | 'wrn' | 'dgr';

interface KpiProps {
  label: string;
  value: ReactNode;
  tom: Tom;
  detail: ReactNode;
  icon: IconName;
  /** Explicação ⓘ ao lado do rótulo (FR-132). */
  tip?: string;
  /** Terceira linha, no estilo do detalhe: a hora da foto do cluster (aim-6ph.13). */
  note?: ReactNode;
}

const TOM_CLASS: Record<Tom, string> = {
  pri: s.vPri,
  ok: s.vOk,
  wrn: s.vWrn,
  dgr: s.vDgr,
};

function Kpi({ label, value, tom, detail, icon, tip, note }: KpiProps) {
  return (
    <section className={s.kpi} aria-label={label}>
      <div className={s.kpiHd}>
        <span className={s.kpiL}>
          {label}
          {tip ? (
            <InfoTip text={tip} label={t('overview.kpi.tipLabel', { label })} size="xs" />
          ) : null}
        </span>
        <Icon name={icon} size="sm" className={s.kpiIcon} aria-hidden="true" />
      </div>
      <strong className={[s.kpiV, TOM_CLASS[tom]].join(' ')}>{value}</strong>
      <span className={s.kpiD}>{detail}</span>
      {note}
    </section>
  );
}

/**
 * O detail do indicador Identidades, que desde 30/08 diz **de onde o número vem** — é isso que o separa do
 * denominador do SecurityReady ao lado.
 *
 * Desde o aim-mng (decisão do mrolim, 11/10/2026) é só isso: a contagem por papel saiu. Ela crescia com o cadastro
 * («1 Atendimento ao cliente · 1 Especialista de faturamento · …») e não cabia num card de um número.
 */
function identitiesDetail(howMany: number): string {
  return t(howMany === 0 ? 'overview.kpi.identities.none' : 'overview.kpi.identities.governed');
}

export interface KpiCardsProps {
  overview: Overview;
  /** A tela pediu a leitura nova do cluster e o pedido não voltou: o SecurityReady já diz «lendo». */
  readingPending?: boolean;
}

export function KpiCards({ overview, readingPending = false }: KpiCardsProps) {
  const { indicators, failures } = overview;
  const pendentes = indicators.pending_approvals ?? 0;
  const failureReady = cardFailure(failures, 'security_ready');
  const failureDeny = cardFailure(failures, 'deny_24h');

  const ready = indicators.security_ready_ok;
  const readyTotal = indicators.security_ready_total;
  const deny = indicators.deny_24h;
  const identidades = indicators.identities ?? 0;

  return (
    <div className={s.kpis}>
      {/* **Os dois primeiros indicators vêm de fontes diferentes e não diziam
          qual** (ensaio de 30/08). A tela abria com `Identidades 0` ao lado de
          `SecurityReady 0/21`, e a primeira pergunta do público era "zero
          identidades, mas 21 do quê?". Os dois números estavam certos; a leitura
          junta é que era contraditória.

          A correção é a mesma do «Ambiente efetivo» em T5: cada número nomeia a
          própria fonte. Aqui, "governadas neste tenant"; ao lado, "workloads
          vistos no cluster". Lidos juntos passam a contar uma história coerente —
          e é a história verdadeira na abertura da apresentação: nenhum dos
          workloads que o cluster mostra está governado ainda. */}
      <Kpi
        label={t('overview.kpi.identities')}
        value={identidades}
        tom="pri"
        detail={identitiesDetail(identidades)}
        icon="robot"
        tip={t('overview.kpi.identities.tip')}
      />

      <Kpi
        label={t('overview.kpi.securityReady')}
        value={
          failureReady || ready === null || ready === undefined ? (
            '—'
          ) : (
            <>
              {ready}
              <span className={s.kpiVMuted}>/{readyTotal ?? 0}</span>
            </>
          )
        }
        tom="ok"
        // O denominador **nomeia a própria fonte**: ele vem do executor
        // (`GET /api/v1/status`), não do nosso banco. São os workloads que o
        // cluster mostra, e é por isso que ele pode ser maior que o indicador ao
        // lado. Sem o total lido, volta a fórmula dos checks — dizer "de 0
        // workloads" quando não se leu nada seria afirmar o que não se sabe.
        detail={
          failureReady
            ? failureText(failureReady)
            : readyTotal === null || readyTotal === undefined
              ? t('overview.kpi.securityReady.checks')
              : tn('overview.kpi.securityReady.of', readyTotal)
        }
        icon="check-circle"
        tip={t('overview.kpi.securityReady.tip')}
        // De quando é a foto do cluster por trás do número (aim-6ph.13, §1.1). Cada bloco de T3 tem a sua hora
        // (decisão 6): este é o L7 agregado; o card Conexão é o `/debug/status`.
        note={
          <ReadingNote
            reading={overview.readiness?.reading}
            pending={readingPending}
            className={s.kpiD}
            testId="security-ready-reading"
          />
        }
      />

      <Kpi
        label={t('overview.kpi.pending')}
        value={pendentes}
        tom={pendentes > 0 ? 'dgr' : 'ok'}
        detail={t(pendentes > 0 ? 'overview.kpi.pending.some' : 'overview.kpi.pending.none')}
        icon="pause"
      />

      <Kpi
        label={t('overview.kpi.deny')}
        value={failureDeny || deny === null || deny === undefined ? '—' : deny}
        tom={!failureDeny && deny ? 'wrn' : 'ok'}
        // O «de {n} decisões» e o número grande vêm da MESMA leitura (o backend
        // decide store × executor para os três contadores juntos). Antes disso
        // seria possível — e invisível — mostrar «4 de 128» com o 4 de uma fonte e
        // o 128 de outra: mesma mistura que fazia o ALLOW do card ao lado inflar,
        // só que num sintoma discreto o bastante para ninguém investigar.
        detail={
          failureDeny || indicators.decisions_24h === null || indicators.decisions_24h === undefined
            ? failureDeny
              ? failureText(failureDeny)
              : t('overview.kpi.deny.unread')
            : tn('overview.kpi.deny.of', indicators.decisions_24h)
        }
        icon="ban"
      />
    </div>
  );
}
